import React, { Fragment, useEffect } from 'react';
import styles from './styles.scss';
import { Redirect, Route, Switch, useParams } from 'react-router-dom';
import Mousetrap from 'mousetrap';

import SessionStore from '../../rx-stores/session';
import { SessionActionTypes, SessionStatus } from '../../types/session';
import useRxStore from '../../utils/use-rx-store';
import useRxDispatch from '../../utils/use-rx-dispatch';
import { deserializeOrganization } from '../../types/all-organizations';

import { Home } from '../Home';
import Navbar from '../Navbar';
import NavbarSidebar from '../Navbar/Sidebar';
import InConfiguration from '../InConfiguration';
import SensorsDetail from '../Sensors/Detail';
import SensorsSensorConfiguration from '../Sensors/SensorConfiguration';
import Implemented from '../Implemented';
import SpaceDetail from '../Spaces/Detail';
import { LoginAuth0 } from '../LoginAuth0';
import Logout from '../Logout';
import GlobalSearch from '../GlobalSearch';
import KeyboardShortcutHelp from '../KeyboardShortcutsHelp';
import { Toaster, ToastContext, Dialogger } from '@densityco/ui';
import MultiPointInstallationList from '../MultiPointInstallation/List';
import MultiPointInstallationCaptureDetails from '../MultiPointInstallation/CaptureDetails';
import MultiPointInstallationCaptures from '../MultiPointInstallation/Captures';
import MultiPointInstallationM2MCaptures from '../MultiPointInstallation/M2MCaptures';
import MultiPointInstallationEdit from '../MultiPointInstallation/Edit';
import MultiPointInstallationCreationStepReorder from '../MultiPointInstallation/CreationStepReorder';
import { ON_PREM } from '../../utils/environment';
import { LoginCore } from '../LoginCore';

const RouteWrapper: React.FunctionComponent<{}> = ({ children }) => {
  const dispatch = useRxDispatch();

  // NOTE: this type is a bit unusual! This value can either be:
  // 1. An organization id
  // 2. The literal string "all" (ALL_ORGANIZATIONS)
  // 3. The value `undefined`, which means the page doesn't have an organization prefix in the url
  const { organizationIdOrAll: organizationIdOrAllOrUndefined } = useParams();

  useEffect(() => {
    dispatch({
      type: SessionActionTypes.SESSION_SET_SELECTED_ORGANIZATION_ID,
      id: deserializeOrganization(organizationIdOrAllOrUndefined),
    });
  }, [organizationIdOrAllOrUndefined]);

  return (
    <div className={styles.verticalWrapper}>
      <Navbar />

      {/* Allows a user to press '?' to open a list of all keyboard shortcuts */}
      <KeyboardShortcutHelp />

      <div className={styles.horizontalWrapper}>
        <NavbarSidebar />
        <div className={styles.content}>{children}</div>
      </div>
    </div>
  );
};

export const App: React.FunctionComponent<{}> = () => {
  const session = useRxStore(SessionStore);
  const dispatch = useRxDispatch();

  // Add keyboard shortcut to open global search (from any page)
  useEffect(() => {
    if (session.status !== SessionStatus.LOGGED_IN) {
      return;
    }
    const mousetrap = new Mousetrap(document);

    mousetrap.bind(['command+k', 'ctrl+k', '/'], () => {
      window.location.href = '#/search';
    });

    return () => mousetrap.reset();
  }, [session]);

  return (
    <Fragment>
      {/* Allows dialogs and toasts to be shown globally */}
      <ToastContext.Provider value="MULTILINE">
        <Toaster />
      </ToastContext.Provider>
      <Dialogger />

      <Switch>
        {/* NOTE: DEPRECATED ROUTES BELOW */}
        <Redirect exact from="/home" to="/" />
        <Redirect exact from="/installations" to="/all/in-configuration" />
        <Redirect exact from="/spaces" to="/all/implemented" />
        <Redirect
          exact
          from="/installations/:sensorSerialNumber"
          to="/all/sensors/:sensorSerialNumber"
        />
        <Redirect
          exact
          from="/spaces/:spaceId"
          to="/all/spaces/:spaceId"
        />
        <Redirect
          exact
          from="/:organizationIdOrAll/installations/:sensorSerialNumber"
          to="/:organizationIdOrAll/sensors/:sensorSerialNumber"
        />
        <Redirect
          exact
          from="/:organizationIdOrAll/installations/:sensorSerialNumber/configure"
          to="/:organizationIdOrAll/sensors/:sensorSerialNumber/configure"
        />

        <Route exact path="/">
          <RouteWrapper>
            <Home />
          </RouteWrapper>
        </Route>
        <Route exact path="/login" component={ON_PREM ? LoginCore : LoginAuth0} />
        <Route exact path="/logout">
          <Logout />
        </Route>
        <Route exact path="/search">
          <RouteWrapper>
            <GlobalSearch />
          </RouteWrapper>
        </Route>

        <Route exact path="/:organizationIdOrAll/sensors/:sensorSerialNumber">
          <RouteWrapper>
            <SensorsDetail />
          </RouteWrapper>
        </Route>
        <Route
          exact
          path="/:organizationIdOrAll/sensors/:sensorSerialNumber/configure"
        >
          <RouteWrapper>
            <SensorsSensorConfiguration />
          </RouteWrapper>
        </Route>
        <Route exact path="/:organizationIdOrAll/in-configuration">
          <RouteWrapper>
            <InConfiguration />
          </RouteWrapper>
        </Route>
        <Route exact path="/:organizationIdOrAll/spaces/:spaceId">
          <RouteWrapper>
            <SpaceDetail />
          </RouteWrapper>
        </Route>
        <Route exact path="/:organizationIdOrAll/implemented">
          <RouteWrapper>
            <Implemented />
          </RouteWrapper>
        </Route>
        <Route exact path="/:organizationIdOrAll/multi-point-installations">
          <RouteWrapper>
            <MultiPointInstallationList />
          </RouteWrapper>
        </Route>
        <Route exact path="/:organizationIdOrAll/multi-point-installations/creation-step-reorder">
          <RouteWrapper>
            <MultiPointInstallationCreationStepReorder />
          </RouteWrapper>
        </Route>
        <Route exact path="/:organizationIdOrAll/multi-point-installations/:mpiId/captures">
          <RouteWrapper>
            <MultiPointInstallationCaptures />
          </RouteWrapper>
        </Route>
        <Route exact path="/:organizationIdOrAll/multi-point-installations/:mpiId/captures/:captureGroupId">
          <RouteWrapper>
            <MultiPointInstallationCaptureDetails />
          </RouteWrapper>
        </Route>
        <Route exact path="/:organizationIdOrAll/multi-point-installations/:mpiId/m2m-captures">
          <RouteWrapper>
            <MultiPointInstallationM2MCaptures />
          </RouteWrapper>
        </Route>
        <Route exact path="/:organizationIdOrAll/multi-point-installations/:mpiId/edit">
          <RouteWrapper>
            <MultiPointInstallationEdit />
          </RouteWrapper>
        </Route>
        <Route path="*">
          <h1>404 Not Found</h1>
        </Route>
      </Switch>
    </Fragment>
  );
};

export default App;
